/**
 * Unified render-status contract — see docs/RENDER_STATUS_CONTRACT.md.
 *
 * One shape (`RenderJobView`) every render/export transport adapts into:
 *   - narrated / parallel narrated  → trigger.dev realtime run (`narratedRunToView`)
 *   - hyperframes MP4 export        → trigger.dev realtime run (`hyperframesExportRunToView`)
 *   - remotion inline export        → `/api/render/video` poll  (`remotionExportPollToView`)
 *   - media studio jobs             → `/api/media/jobs` summary (`mediaJobToView`)
 *
 * CLIENT-SAFE: this module must never import the db, env, or anything
 * server-only. Adapters are pure mappers; `cancel` closures hit HTTP
 * endpoints. It is also imported by client components rendered inside chat,
 * so keep it dependency-free.
 */

export type RenderJobStatus = 'queued' | 'running' | 'completed' | 'failed' | 'canceled';

export interface RenderJobView {
  /** Stable id within its pipeline (trigger runId, executionArn, media job id, renderId). */
  id: string;
  kind: 'narrated' | 'remotion-export' | 'hyperframes-export' | 'ai-clip' | 'media-job';
  status: RenderJobStatus;
  /** Machine stage from the pipeline, e.g. 'generating-code' | 'rendering-video'. */
  stage?: string;
  /** Human label for the stage — the adapter owns the wording, not the card. */
  stageLabel?: string;
  /** 0..1. Omit when indeterminate — the card shows a shimmer, not a fake bar. */
  progress?: number;
  outputUrl?: string;
  posterUrl?: string;
  error?: string;
  /** True only when cancel will actually work NOW (e.g. queued trigger run). */
  canCancel: boolean;
  cancel?: () => Promise<void>;
  /** True when a failed job can be retried in place. */
  canRetry: boolean;
  meta?: { widthPx?: number; heightPx?: number; fps?: number; videoDurationSec?: number };
}

// ---------------------------------------------------------------------------
// trigger.dev realtime runs (narrated + hyperframes-export)
// ---------------------------------------------------------------------------

/**
 * Structural subset of the run object `useRealtimeRun` returns. Kept
 * structural so this module doesn't couple to trigger.dev types.
 */
export interface TriggerRealtimeRunLike {
  id?: string;
  status: string;
  metadata?: Record<string, unknown>;
  output?: unknown;
}

/** trigger.dev v4 RunStatus buckets. Unknown statuses map to 'running'. */
const TRIGGER_QUEUED_STATUSES = new Set(['PENDING_VERSION', 'QUEUED', 'DELAYED']);
const TRIGGER_FAILED_STATUSES = new Set(['FAILED', 'CRASHED', 'SYSTEM_FAILURE', 'TIMED_OUT', 'EXPIRED']);

function triggerStatusBucket(status: string): RenderJobStatus {
  if (status === 'COMPLETED') return 'completed';
  if (status === 'CANCELED') return 'canceled';
  if (TRIGGER_QUEUED_STATUSES.has(status)) return 'queued';
  if (TRIGGER_FAILED_STATUSES.has(status)) return 'failed';
  return 'running';
}

function triggerFailureMessage(status: string): string {
  return `Task ${status.toLowerCase().replace(/_/g, ' ')}`;
}

function readTriggerMeta(run: TriggerRealtimeRunLike | undefined): {
  stage?: string;
  progress?: number;
  renderError?: string;
} {
  const meta = (run?.metadata ?? {}) as { stage?: unknown; progress?: unknown; renderError?: unknown };
  return {
    stage: typeof meta.stage === 'string' ? meta.stage : undefined,
    progress: typeof meta.progress === 'number' ? Math.max(0, Math.min(1, meta.progress / 100)) : undefined,
    renderError: typeof meta.renderError === 'string' ? meta.renderError : undefined,
  };
}

async function postCancel(url: string): Promise<void> {
  const res = await fetch(url, { method: 'POST' });
  if (!res.ok) {
    const data = await res.json().catch(() => undefined) as { error?: string } | undefined;
    throw new Error(data?.error ?? `Cancel failed (${res.status})`);
  }
}

const NARRATED_STAGE_LABELS: Record<string, string> = {
  'generating-code': 'Subagent writing scene code…',
  'generating-scenes': 'Parallel subagents writing scenes…',
  'rendering-video': 'Rendering frames on Lambda…',
  'saving': 'Finalizing & saving…',
  'done': 'Done',
};

/**
 * Adapter: narrated / parallel-narrated trigger run → RenderJobView.
 *
 * Narrated semantics: a COMPLETED run with `renderError` is still
 * 'completed' — the componentCode was saved and plays in-browser; only the
 * Lambda MP4 failed. The error field carries that nuance to the card.
 *
 * Cancel: queued runs only, via POST /api/render/narrated/[runId]/cancel
 * (trigger `runs.cancel`; credits/work only start at EXECUTING).
 */
export function narratedRunToView(
  taskId: string,
  run: TriggerRealtimeRunLike | undefined,
  opts?: { subscribeError?: Error; videoDurationSec?: number },
): RenderJobView {
  const base = {
    id: taskId,
    kind: 'narrated' as const,
    canRetry: false,
    meta: opts?.videoDurationSec ? { videoDurationSec: opts.videoDurationSec } : undefined,
  };

  if (opts?.subscribeError) {
    return {
      ...base,
      status: 'failed',
      error: `Couldn't track the render: ${opts.subscribeError.message}`,
      canCancel: false,
    };
  }

  if (!run) {
    return { ...base, status: 'queued', stageLabel: 'Starting…', canCancel: false };
  }

  const status = triggerStatusBucket(run.status);
  const { stage, progress, renderError } = readTriggerMeta(run);
  const output = run.output as { videoUrl?: string; renderError?: string } | undefined;

  if (status === 'completed') {
    return {
      ...base,
      status: 'completed',
      stage: 'done',
      progress: 1,
      outputUrl: output?.videoUrl,
      error: output?.renderError ?? renderError,
      canCancel: false,
    };
  }
  if (status === 'failed' || status === 'canceled') {
    return {
      ...base,
      status,
      error: status === 'canceled' ? 'Render canceled' : triggerFailureMessage(run.status),
      canCancel: false,
    };
  }

  // Parallel narrated runs report per-scene fan-out counts — surface them as
  // the stage label (the adapter owns the wording, not the card).
  const sceneMeta = (run.metadata ?? {}) as { completedScenes?: unknown; totalScenes?: unknown };
  const scenesLabel =
    stage === 'generating-scenes'
      && typeof sceneMeta.completedScenes === 'number'
      && typeof sceneMeta.totalScenes === 'number'
      ? `Scenes written: ${sceneMeta.completedScenes}/${sceneMeta.totalScenes}`
      : undefined;

  const canCancel = status === 'queued';
  return {
    ...base,
    status,
    stage,
    stageLabel: status === 'queued'
      ? 'Queued for rendering…'
      : scenesLabel ?? NARRATED_STAGE_LABELS[stage ?? ''] ?? 'Working…',
    progress,
    canCancel,
    cancel: canCancel
      ? () => postCancel(`/api/render/narrated/${encodeURIComponent(taskId)}/cancel`)
      : undefined,
  };
}

const HYPERFRAMES_EXPORT_STAGE_LABELS: Record<string, string> = {
  'deploying': 'Uploading composition…',
  'starting-render': 'Starting Lambda render…',
  'rendering': 'Rendering frames on Lambda…',
  'done': 'Finalizing…',
};

/**
 * Adapter: render-hyperframes-export trigger run → RenderJobView.
 *
 * The task is catch-not-throw, so a COMPLETED run with `renderError` means
 * the export itself FAILED (unlike narrated, where the in-browser video
 * survives a Lambda failure) — map it to 'failed' with canRetry.
 *
 * Cancel: queued runs only, via POST /api/render/hyperframes/cancel
 * (trigger `runs.cancel` — see the contract doc for why not SFN
 * StopExecution in v1).
 */
export function hyperframesExportRunToView(
  taskId: string,
  run: TriggerRealtimeRunLike | undefined,
  opts?: { subscribeError?: Error },
): RenderJobView {
  const base = { id: taskId, kind: 'hyperframes-export' as const };

  if (opts?.subscribeError) {
    return {
      ...base,
      status: 'failed',
      error: `Couldn't track the export: ${opts.subscribeError.message}`,
      canCancel: false,
      canRetry: true,
    };
  }

  if (!run) {
    return { ...base, status: 'queued', stageLabel: 'Starting export…', canCancel: false, canRetry: false };
  }

  const status = triggerStatusBucket(run.status);
  const { stage, progress, renderError: metaRenderError } = readTriggerMeta(run);
  const output = run.output as { outputUrl?: string; renderError?: string } | undefined;

  if (status === 'completed') {
    const renderError = output?.renderError ?? metaRenderError;
    if (renderError || !output?.outputUrl) {
      return {
        ...base,
        status: 'failed',
        error: renderError ?? 'Export finished without an output URL',
        canCancel: false,
        canRetry: true,
      };
    }
    return {
      ...base,
      status: 'completed',
      stage: 'done',
      progress: 1,
      outputUrl: output.outputUrl,
      canCancel: false,
      canRetry: false,
    };
  }
  if (status === 'failed' || status === 'canceled') {
    return {
      ...base,
      status,
      error: status === 'canceled' ? 'Export canceled' : triggerFailureMessage(run.status),
      canCancel: false,
      canRetry: true,
    };
  }

  const canCancel = status === 'queued';
  return {
    ...base,
    status,
    stage,
    stageLabel: status === 'queued'
      ? 'Queued for rendering…'
      : HYPERFRAMES_EXPORT_STAGE_LABELS[stage ?? ''] ?? 'Rendering…',
    progress,
    canCancel,
    cancel: canCancel
      ? () => postCancel(`/api/render/hyperframes/cancel?runId=${encodeURIComponent(taskId)}`)
      : undefined,
    canRetry: false,
  };
}

// ---------------------------------------------------------------------------
// remotion inline export (poll POST/GET /api/render/video)
// ---------------------------------------------------------------------------

/** Structural subset of the GET /api/render/video poll response. */
export interface RemotionExportPollLike {
  done?: boolean;
  /** Public MP4 URL when done. */
  outputFile?: string | null;
  /** Remotion Lambda progress, already 0..1. */
  overallProgress?: number;
  fatalErrorEncountered?: boolean;
  errors?: Array<{ message?: string }>;
}

/**
 * Adapter: remotion inline export → RenderJobView.
 *
 * The transport is a blocking POST followed by client-side polling —
 * there is no queue phase and no cancel (the Lambda render can't be
 * stopped from here; showing a cancel button would be a dead button).
 */
export function remotionExportPollToView(
  renderId: string,
  poll: RemotionExportPollLike | undefined,
  opts?: { error?: string },
): RenderJobView {
  const base = { id: renderId, kind: 'remotion-export' as const, canCancel: false };

  if (opts?.error) {
    return { ...base, status: 'failed', error: opts.error, canRetry: true };
  }
  if (!poll) {
    return { ...base, status: 'running', stageLabel: 'Starting Lambda render…', canRetry: false };
  }
  if (poll.fatalErrorEncountered) {
    return {
      ...base,
      status: 'failed',
      error: poll.errors?.[0]?.message ?? 'Render failed',
      canRetry: true,
    };
  }
  if (poll.done && poll.outputFile) {
    return { ...base, status: 'completed', progress: 1, outputUrl: poll.outputFile, canRetry: false };
  }
  return {
    ...base,
    status: 'running',
    stageLabel: 'Rendering frames on Lambda…',
    progress: typeof poll.overallProgress === 'number'
      ? Math.max(0, Math.min(1, poll.overallProgress))
      : undefined,
    canRetry: false,
  };
}

// ---------------------------------------------------------------------------
// media studio jobs (/api/media/jobs → summarizeMediaJob)
// ---------------------------------------------------------------------------

/** Structural subset of `summarizeMediaJob` (lib/media-jobs.ts). */
export interface MediaJobSummaryLike {
  id: string;
  status: 'queued' | 'running' | 'completed' | 'failed' | 'refunded' | 'canceled' | string;
  progressMessage?: string | null;
  errorMessage?: string | null;
  output?: Record<string, unknown> | null;
}

/**
 * Adapter: media studio job → RenderJobView.
 *
 * 'refunded' maps to 'failed' (the job failed and credits were returned —
 * the card should read as a failure, the refund is a detail). Cancel
 * mirrors the media-jobs precedent exactly: queued only — running jobs are
 * charged at the provider, so no dead cancel button.
 */
export function mediaJobToView(job: MediaJobSummaryLike): RenderJobView {
  const canCancel = job.status === 'queued';
  const output = job.output ?? undefined;
  const outputUrl =
    typeof output?.url === 'string' ? output.url
    : typeof output?.outputUrl === 'string' ? output.outputUrl
    : undefined;

  const status: RenderJobStatus =
    job.status === 'queued' ? 'queued'
    : job.status === 'running' ? 'running'
    : job.status === 'completed' ? 'completed'
    : job.status === 'canceled' ? 'canceled'
    : 'failed'; // 'failed' | 'refunded' | unknown

  return {
    id: job.id,
    kind: 'media-job',
    status,
    stageLabel: job.progressMessage ?? undefined,
    outputUrl,
    error: status === 'failed' ? job.errorMessage ?? undefined : undefined,
    canCancel,
    cancel: canCancel
      ? () => postCancel(`/api/media/jobs/${encodeURIComponent(job.id)}/cancel`)
      : undefined,
    canRetry: status === 'failed', // POST /api/media/jobs/[id]/retry exists
  };
}
